import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';
import {
  canAccessPage,
  firstAllowedPath,
  hasPermission,
  membershipForOrg,
  permissionsForOrg,
  resolveSelectedOrgId,
} from '../services/permissions';

/**
 * The signed-in user's permissions in the organization currently selected in the header pulldown.
 * Re-evaluates whenever the pulldown changes (SELECTED_ORGANIZATION_EVENT) or the user reloads.
 */
export default function usePermissions() {
  const { user } = useAuth();
  const [cookieOrgId, setCookieOrgId] = useState(() => organizationsService.getSelectedOrganization('id'));

  useEffect(() => {
    const sync = () => setCookieOrgId(organizationsService.getSelectedOrganization('id'));
    sync();
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, sync);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, sync);
  }, []);

  const orgId = useMemo(() => resolveSelectedOrgId(user, cookieOrgId), [user, cookieOrgId]);
  const membership = useMemo(() => membershipForOrg(user, orgId), [user, orgId]);
  const permissions = useMemo(() => permissionsForOrg(user, orgId), [user, orgId]);

  const can = useCallback((permission) => hasPermission(user, orgId, permission), [user, orgId]);
  const canOpen = useCallback((pathname) => canAccessPage(user, orgId, pathname), [user, orgId]);

  return {
    orgId,
    orgName: membership?.organization_name ?? null,
    roles: membership?.roles ?? [],
    permissions,
    can,
    canOpen,
    isPlatformAdmin: Boolean(user?.is_admin),
    fallbackPath: firstAllowedPath(user, orgId),
  };
}
